<app-admin-page-shell
  titleKey="app.admin.patrol.title"
  subtitleKey="app.admin.patrol.subtitle"
  [panelled]="false"
>
  <div class="patrol-admin-content">
    <section class="platform-flags" aria-label="Patrol platform availability">
      <div class="platform-flags__header">
        <h2 class="platform-flags__title">
          {{ 'app.admin.patrol.platformFlags.title' | translate }}
        </h2>
        <p class="platform-flags__hint">
          {{ 'app.admin.patrol.platformFlags.hint' | translate }}
        </p>
      </div>

      @if (platformFlagsLoading()) {
        <p class="platform-flags__loading">
          {{ 'app.admin.patrol.platformFlags.loading' | translate }}
        </p>
      } @else if (platformFlagsError()) {
        <div class="platform-flags__error">
          <p>{{ 'app.admin.patrol.platformFlags.error' | translate }}</p>
          <app-cta-button size="small" (clicked)="loadPlatformFlags()">
            <span cta-label>{{ 'app.admin.patrol.retry' | translate }}</span>
          </app-cta-button>
        </div>
      } @else {
        @if (platformFlags(); as flags) {
        <ul class="platform-flags__list">
          <li class="platform-flag">
            <div class="platform-flag__copy">
              <p class="platform-flag__name">
                {{ 'app.admin.patrol.platformFlags.discord.name' | translate }}
              </p>
              <p class="platform-flag__description">
                {{
                  'app.admin.patrol.platformFlags.discord.description'
                    | translate
                }}
              </p>
            </div>
            <app-admin-toggle-switch
              [checked]="flags.discord"
              [disabled]="isPlatformToggleBusy('discord')"
              [ariaLabel]="
                'app.admin.patrol.platformFlags.discord.name' | translate
              "
              (checkedChange)="toggleDiscordPlatform($event)"
            />
          </li>
          <li class="platform-flag">
            <div class="platform-flag__copy">
              <p class="platform-flag__name">
                {{ 'app.admin.patrol.platformFlags.roblox.name' | translate }}
              </p>
              <p class="platform-flag__description">
                {{
                  'app.admin.patrol.platformFlags.roblox.description'
                    | translate
                }}
              </p>
            </div>
            <app-admin-toggle-switch
              [checked]="flags.roblox"
              [disabled]="isPlatformToggleBusy('roblox')"
              [ariaLabel]="
                'app.admin.patrol.platformFlags.roblox.name' | translate
              "
              (checkedChange)="toggleRobloxPlatform($event)"
            />
          </li>
        </ul>
        }
      }
    </section>

    <section class="fleet-monitor" aria-label="Patrol fleet monitor">
      <div class="fleet-monitor__header">
        <div>
          <h2 class="fleet-monitor__title">
            {{ 'app.admin.patrol.fleetTabs.label' | translate }}
          </h2>
          <p class="fleet-monitor__hint">
            {{ 'app.admin.patrol.fleetTabs.hint' | translate }}
          </p>
        </div>
        <app-cta-button variant="plain" size="small" (clicked)="refreshStatus()">
          <span cta-label>{{ 'app.admin.patrol.refresh' | translate }}</span>
        </app-cta-button>
      </div>

      <nav class="fleet-tabs" aria-label="Fleet platform tabs">
        @for (tab of fleetTabs; track tab.id) {
          <button
            type="button"
            class="fleet-tabs__btn"
            [class.fleet-tabs__btn--active]="isFleetTabActive(tab.id)"
            [attr.aria-selected]="isFleetTabActive(tab.id)"
            role="tab"
            (click)="setFleetTab(tab.id)"
          >
            {{ tab.labelKey | translate }}
            @if (tab.id === 'discord' && fleet(); as discordFleet) {
              <span class="fleet-tabs__badge">{{ discordFleet.guildCount }}</span>
            }
            @if (tab.id === 'roblox' && robloxFleet(); as rbxFleet) {
              <span class="fleet-tabs__badge">{{ rbxFleet.experienceCount }}</span>
            }
          </button>
        }
      </nav>

      <div
        class="fleet-tab-panel"
        role="tabpanel"
        [attr.aria-label]="
          (activeFleetTab() === 'discord'
            ? 'app.admin.patrol.fleetTabs.discord'
            : 'app.admin.patrol.fleetTabs.roblox'
          ) | translate
        "
      >
        @if (activeFleetTab() === 'discord') {
    <div class="platform-panel platform-panel--discord" aria-label="Discord Patrol">
    @if (isDiscordLoading()) {
      <div class="loading-state">
        <mat-spinner diameter="40"></mat-spinner>
        <p>{{ 'app.admin.patrol.loading' | translate }}</p>
      </div>
    } @else if (isDiscordError()) {
      <div class="status-card status-card--error">
        <h2 class="status-card__title">
          {{ 'app.admin.patrol.errorTitle' | translate }}
        </h2>
        <p class="status-card__text">
          {{ 'app.admin.patrol.errorText' | translate }}
        </p>
        <app-cta-button size="small" (clicked)="refreshStatus()">
          <span cta-label>{{ 'app.admin.patrol.retry' | translate }}</span>
        </app-cta-button>
      </div>
    } @else if (botInstalled()) {
      <section class="fleet-summary" aria-label="Bot health">
        <div class="fleet-summary__card">
          <div class="fleet-summary__lead">
            <span
              class="fleet-summary__dot"
              [class.fleet-summary__dot--online]="fleet()?.botReady"
              [class.fleet-summary__dot--offline]="!fleet()?.botReady"
              aria-hidden="true"
            ></span>
            <div class="fleet-summary__copy">
              <p class="fleet-summary__label">
                {{ 'app.admin.patrol.fleet.botStatusLabel' | translate }}
              </p>
              <p class="fleet-summary__title">
                {{
                  fleet()?.loggedInAs ??
                    ('app.admin.patrol.fleet.botNotReady' | translate)
                }}
              </p>
              <p class="fleet-summary__meta">
                <span
                  class="fleet-summary__status"
                  [class.fleet-summary__status--online]="fleet()?.botReady"
                >
                  {{
                    (fleet()?.botReady
                      ? 'app.admin.patrol.fleet.botReady'
                      : 'app.admin.patrol.fleet.botNotReady'
                    ) | translate
                  }}
                </span>
                <span class="fleet-summary__sep" aria-hidden="true">·</span>
                <span>{{
                  'app.admin.patrol.fleet.discordGuildCount'
                    | translate: { count: fleet()?.guildCount ?? 0 }
                }}</span>
                @if (fleet()?.allowlistEnforced) {
                  <span class="fleet-summary__sep" aria-hidden="true">·</span>
                  <span class="fleet-summary__allowlist">{{
                    'app.admin.patrol.fleet.allowlistOn' | translate
                  }}</span>
                }
              </p>
            </div>
          </div>
          <app-cta-button
            class="fleet-summary__refresh"
            variant="plain"
            size="small"
            (clicked)="refreshStatus()"
          >
            <span cta-label>{{ 'app.admin.patrol.refresh' | translate }}</span>
          </app-cta-button>
        </div>
        @if (fleet()?.startupError) {
          <p class="fleet-summary__warning">
            {{ 'app.admin.patrol.startupWarning' | translate }}
          </p>
        }
        @if (allowlistErrorKey(); as errorKey) {
          <p class="fleet-summary__warning">{{ errorKey | translate }}</p>
        }
      </section>

      <section class="fleet-section">
        <h2 class="fleet-section__title">
          {{ 'app.admin.patrol.fleet.registeredTitle' | translate }}
        </h2>
        @if (registeredGuilds().length === 0) {
          <p class="fleet-empty">
            {{ 'app.admin.patrol.fleet.noRegisteredGuilds' | translate }}
          </p>
        } @else {
          <div class="fleet-table-wrap">
            <table class="fleet-table fleet-table--registered">
              <thead>
                <tr>
                  <th>{{ 'app.admin.patrol.fleet.columns.server' | translate }}</th>
                  <th>{{ 'app.admin.patrol.fleet.columns.guildId' | translate }}</th>
                  <th>{{ 'app.admin.patrol.fleet.columns.status' | translate }}</th>
                  <th>{{ 'app.admin.patrol.fleet.columns.members' | translate }}</th>
                  <th>{{ 'app.admin.patrol.fleet.columns.linked' | translate }}</th>
                  <th>{{ 'app.admin.patrol.fleet.columns.bot' | translate }}</th>
                  <th>{{ 'app.admin.patrol.fleet.columns.allowlist' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (guild of registeredGuilds(); track guild.registryId) {
                  <tr>
                    <td>{{ guild.guildName }}</td>
                    <td>
                      <code class="fleet-id">{{ guild.guildId ?? '—' }}</code>
                    </td>
                    <td>
                      <span class="fleet-status" [attr.data-status]="guild.status">
                        {{ statusLabelKey(guild.status) | translate }}
                      </span>
                    </td>
                    <td>{{ guild.memberCount }}</td>
                    <td>{{ guild.linkedMemberCount }}</td>
                    <td>
                      {{
                        (guild.botPresent
                          ? 'app.admin.patrol.fleet.botPresent'
                          : 'app.admin.patrol.fleet.botAbsent'
                        ) | translate
                      }}
                    </td>
                    <td class="fleet-actions">
                      <div class="fleet-actions__content">
                      @if (guild.guildId) {
                        @if (guild.allowlisted) {
                          <span class="fleet-allowlist fleet-allowlist--on">
                            {{ 'app.admin.patrol.fleet.allowlisted' | translate }}
                          </span>
                          <app-cta-button
                            variant="link"
                            size="small"
                            [minHeightDisabled]="true"
                            [loading]="isAllowlistBusy(guild.guildId)"
                            (clicked)="removeGuildFromAllowlist(guild.guildId)"
                          >
                            <span cta-label>{{
                              'app.admin.patrol.fleet.removeAllowlist' | translate
                            }}</span>
                          </app-cta-button>
                        } @else {
                          <app-cta-button
                            class="fleet-actions__btn"
                            variant="plain"
                            size="small"
                            [loading]="isAllowlistBusy(guild.guildId)"
                            (clicked)="allowGuild(guild.guildId)"
                          >
                            <span cta-label>{{
                              'app.admin.patrol.fleet.allowGuild' | translate
                            }}</span>
                          </app-cta-button>
                        }
                      } @else {
                        <span class="fleet-allowlist fleet-allowlist--pending">—</span>
                      }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>

      @if (unregisteredGuilds().length > 0) {
        <section class="fleet-section fleet-section--warn">
          <h2 class="fleet-section__title">
            {{ 'app.admin.patrol.fleet.unregisteredTitle' | translate }}
          </h2>
          <p class="fleet-section__hint">
            {{ 'app.admin.patrol.fleet.unregisteredHint' | translate }}
          </p>
          <div class="fleet-table-wrap">
            <table class="fleet-table">
              <thead>
                <tr>
                  <th>{{ 'app.admin.patrol.fleet.columns.server' | translate }}</th>
                  <th>{{ 'app.admin.patrol.fleet.columns.guildId' | translate }}</th>
                  <th>{{ 'app.admin.patrol.fleet.columns.members' | translate }}</th>
                  <th>{{ 'app.admin.patrol.fleet.columns.allowlist' | translate }}</th>
                </tr>
              </thead>
              <tbody>
                @for (guild of unregisteredGuilds(); track guild.guildId) {
                  <tr>
                    <td>{{ guild.guildName }}</td>
                    <td><code class="fleet-id">{{ guild.guildId }}</code></td>
                    <td>{{ guild.memberCount }}</td>
                    <td class="fleet-actions">
                      <div class="fleet-actions__content">
                      @if (guild.allowlisted) {
                        <span class="fleet-allowlist fleet-allowlist--on">
                          {{ 'app.admin.patrol.fleet.allowlisted' | translate }}
                        </span>
                        <app-cta-button
                          variant="link"
                          size="small"
                          [minHeightDisabled]="true"
                          [loading]="isAllowlistBusy(guild.guildId)"
                          (clicked)="removeGuildFromAllowlist(guild.guildId)"
                        >
                          <span cta-label>{{
                            'app.admin.patrol.fleet.removeAllowlist' | translate
                          }}</span>
                        </app-cta-button>
                      } @else {
                        <app-cta-button
                          class="fleet-actions__btn"
                          variant="plain"
                          size="small"
                          [loading]="isAllowlistBusy(guild.guildId)"
                          (clicked)="allowGuild(guild.guildId)"
                        >
                          <span cta-label>{{
                            'app.admin.patrol.fleet.allowGuild' | translate
                          }}</span>
                        </app-cta-button>
                      }
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      }

    } @else {
      <div class="status-card status-card--setup">
        <div class="status-card__icon status-card__icon--shield" aria-hidden="true">
          <img src="/public/svgs/patrol-w.svg" alt="" width="48" height="48" />
        </div>
        <h2 class="status-card__title">
          {{ 'app.admin.patrol.setupTitle' | translate }}
        </h2>
        <p class="status-card__text">
          {{ 'app.admin.patrol.setupText' | translate }}
        </p>
        @if (setup()?.startupError || fleet()?.startupError) {
          <p class="status-card__warning">
            {{ 'app.admin.patrol.startupWarning' | translate }}
          </p>
        }
        <div class="status-card__actions">
          <app-cta-button size="small" (clicked)="addBotToServer()">
            <span cta-label>{{ 'app.admin.patrol.addBot' | translate }}</span>
          </app-cta-button>
        </div>
        @if (generatedInviteUrl(); as url) {
          <div class="invite-url">
            <span class="invite-url__label">{{ 'app.admin.patrol.inviteUrl' | translate }}</span>
            <code class="invite-url__value">{{ url }}</code>
          </div>
        }
        @if (inviteErrorKey(); as errorKey) {
          <p class="status-card__warning">{{ errorKey | translate }}</p>
        }
      </div>
    }
    </div>
        }

        @if (activeFleetTab() === 'roblox') {
    <div class="platform-panel platform-panel--roblox" aria-label="Roblox Patrol">
      @if (isRobloxFleetLoading()) {
        <div class="loading-state loading-state--compact">
          <mat-spinner diameter="32"></mat-spinner>
          <p>{{ 'app.admin.patrol.robloxFleet.loading' | translate }}</p>
        </div>
      } @else if (isRobloxFleetError()) {
        <div class="status-card status-card--error">
          <p class="status-card__text">
            {{ 'app.admin.patrol.robloxFleet.error' | translate }}
          </p>
          <app-cta-button size="small" (clicked)="refreshStatus()">
            <span cta-label>{{ 'app.admin.patrol.retry' | translate }}</span>
          </app-cta-button>
        </div>
      } @else {
        @if (robloxFleet(); as rbx) {
        <section class="fleet-summary" aria-label="Roblox ingest status">
          <div class="fleet-summary__card">
            <div class="fleet-summary__lead">
              <span
                class="fleet-summary__dot"
                [class.fleet-summary__dot--online]="robloxIngestEffective()"
                [class.fleet-summary__dot--offline]="!robloxIngestEffective()"
                aria-hidden="true"
              ></span>
              <div class="fleet-summary__copy">
                <p class="fleet-summary__label">
                  {{ 'app.admin.patrol.robloxFleet.ingestLabel' | translate }}
                </p>
                <p class="fleet-summary__title">
                  {{ robloxIngestStatusKey() | translate }}
                </p>
                <p class="fleet-summary__meta">
                  <span>{{
                    'app.admin.patrol.robloxFleet.experienceCount'
                      | translate: { count: rbx.experienceCount }
                  }}</span>
                  <span class="fleet-summary__sep" aria-hidden="true">·</span>
                  @if (rbx.allowlistEnforced) {
                    <span class="fleet-summary__allowlist">{{
                      'app.admin.patrol.robloxFleet.allowlist.enforced'
                        | translate
                    }}</span>
                  } @else {
                    <span>{{
                      'app.admin.patrol.robloxFleet.allowlist.open' | translate
                    }}</span>
                  }
                </p>
              </div>
            </div>
            <app-cta-button
              class="fleet-summary__refresh"
              variant="plain"
              size="small"
              (clicked)="refreshStatus()"
            >
              <span cta-label>{{ 'app.admin.patrol.refresh' | translate }}</span>
            </app-cta-button>
          </div>
        </section>

        <section class="universe-allowlist" aria-label="Roblox universe allowlist">
          <div class="universe-allowlist__header">
            <div class="universe-allowlist__heading">
              <h3 class="universe-allowlist__title">
                {{ 'app.admin.patrol.robloxFleet.allowlist.title' | translate }}
              </h3>
              <p class="universe-allowlist__hint">
                {{ 'app.admin.patrol.robloxFleet.allowlist.hint' | translate }}
              </p>
            </div>
            <span
              class="access-pill universe-allowlist__status"
              [class.access-pill--allowed]="!rbx.allowlistEnforced"
              [class.universe-allowlist__status--enforced]="rbx.allowlistEnforced"
            >
              {{
                (rbx.allowlistEnforced
                  ? 'app.admin.patrol.robloxFleet.allowlist.enforced'
                  : 'app.admin.patrol.robloxFleet.allowlist.open'
                ) | translate
              }}
            </span>
          </div>

          @if (allowlistErrorKey(); as errorKey) {
            <p class="universe-allowlist__error">{{ errorKey | translate }}</p>
          }

          @if (rbx.allowlistEnforced) {
            @if (robloxAllowlistChips().length === 0) {
              <p class="universe-allowlist__empty">
                {{ 'app.admin.patrol.robloxFleet.allowlist.emptyEnforced' | translate }}
              </p>
            } @else {
              <ul class="allowlist-chips" aria-label="Allowed universe IDs">
                @for (chip of robloxAllowlistChips(); track chip.universeId) {
                  <li class="allowlist-chip">
                    <div class="allowlist-chip__main">
                      <code class="allowlist-chip__id">{{ chip.universeId }}</code>
                      @if (chip.experienceName) {
                        <span class="allowlist-chip__name">{{
                          chip.experienceName
                        }}</span>
                      }
                    </div>
                    <app-cta-button
                      class="allowlist-chip__remove"
                      variant="link"
                      size="small"
                      [minHeightDisabled]="true"
                      [loading]="isUniverseAllowlistBusy(chip.universeId)"
                      (clicked)="removeUniverseFromAllowlist(chip.universeId)"
                    >
                      <span cta-label>{{
                        'app.admin.patrol.robloxFleet.allowlist.removeUniverse'
                          | translate
                      }}</span>
                    </app-cta-button>
                  </li>
                }
              </ul>
            }
            @if (robloxAllowlistPendingCount() > 0) {
              <p class="universe-allowlist__pending">
                {{
                  'app.admin.patrol.robloxFleet.allowlist.pendingCount'
                    | translate: { count: robloxAllowlistPendingCount() }
                }}
              </p>
            }
          } @else {
            <p class="universe-allowlist__open">
              {{ 'app.admin.patrol.robloxFleet.allowlist.openDescription' | translate }}
            </p>
          }
        </section>

        <section class="fleet-section fleet-section--roblox">
          <h3 class="fleet-section__title">
            {{ 'app.admin.patrol.robloxFleet.registeredTitle' | translate }}
          </h3>
          @if (registeredExperiences().length === 0) {
            <p class="fleet-empty">
              {{ 'app.admin.patrol.robloxFleet.noRegisteredExperiences' | translate }}
            </p>
          } @else {
            <div class="fleet-table-wrap">
              <table class="fleet-table fleet-table--registered">
                <thead>
                  <tr>
                    <th>{{ 'app.admin.patrol.robloxFleet.columns.experience' | translate }}</th>
                    <th>{{ 'app.admin.patrol.robloxFleet.columns.universeId' | translate }}</th>
                    <th>{{ 'app.admin.patrol.robloxFleet.columns.status' | translate }}</th>
                    <th>{{ 'app.admin.patrol.robloxFleet.columns.linked' | translate }}</th>
                    <th>{{ 'app.admin.patrol.robloxFleet.columns.bridge' | translate }}</th>
                    <th>{{ 'app.admin.patrol.robloxFleet.columns.access' | translate }}</th>
                    <th>{{ 'app.admin.patrol.robloxFleet.columns.allowlist' | translate }}</th>
                  </tr>
                </thead>
                <tbody>
                  @for (
                    experience of registeredExperiences();
                    track experience.registryId
                  ) {
                    <tr>
                      <td>{{ experience.experienceName }}</td>
                      <td>
                        <code class="fleet-id">{{
                          experience.universeId ?? '—'
                        }}</code>
                      </td>
                      <td>
                        <span
                          class="fleet-status"
                          [attr.data-status]="experience.status"
                        >
                          {{ statusLabelKey(experience.status) | translate }}
                        </span>
                      </td>
                      <td>{{ experience.linkedMemberCount }}</td>
                      <td>
                        <span
                          class="bridge-status"
                          [attr.data-bridge-status]="experience.bridgeStatus ?? 'never_seen'"
                        >
                          {{
                            bridgeStatusLabelKey(experience.bridgeStatus)
                              | translate
                          }}
                        </span>
                        <span class="bridge-last-seen">{{
                          formatBridgeLastSeen(experience.bridgeLastSeenAt)
                        }}</span>
                      </td>
                      <td>
                        @if (!robloxIngestEnvEnabled()) {
                          <span class="access-pill access-pill--disabled">
                            {{
                              'app.admin.patrol.robloxFleet.access.ingestOff'
                                | translate
                            }}
                          </span>
                        } @else if (!robloxPlatformEnabled()) {
                          <span class="access-pill access-pill--disabled">
                            {{
                              'app.admin.patrol.robloxFleet.access.platformOff'
                                | translate
                            }}
                          </span>
                        } @else if (experience.ingestAllowed) {
                          <span class="access-pill access-pill--allowed">
                            {{
                              'app.admin.patrol.robloxFleet.access.allowed'
                                | translate
                            }}
                          </span>
                        } @else {
                          <span class="access-pill access-pill--blocked">
                            {{
                              'app.admin.patrol.robloxFleet.access.blocked'
                                | translate
                            }}
                          </span>
                        }
                      </td>
                      <td class="fleet-actions">
                        <div class="fleet-actions__content fleet-actions__content--inline">
                        @if (experience.universeId) {
                          @if (experience.allowlisted) {
                            <span class="access-pill access-pill--allowed">
                              {{
                                'app.admin.patrol.robloxFleet.allowlist.onAllowlist'
                                  | translate
                              }}
                            </span>
                          } @else if (rbx.allowlistEnforced) {
                            <button
                              type="button"
                              class="access-pill access-pill--action"
                              [disabled]="isUniverseAllowlistBusy(experience.universeId)"
                              (click)="allowUniverse(experience.universeId)"
                            >
                              {{
                                'app.admin.patrol.robloxFleet.allowlist.allowUniverse'
                                  | translate
                              }}
                            </button>
                          } @else {
                            <span class="access-pill access-pill--open">
                              {{
                                'app.admin.patrol.robloxFleet.allowlist.openAccess'
                                  | translate
                              }}
                            </span>
                          }
                        } @else {
                          <span class="fleet-allowlist fleet-allowlist--pending">—</span>
                        }
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </section>
        }
      }
    </div>
        }
      </div>
    </section>
  </div>
</app-admin-page-shell>
